/*
 * box-option.js — keeps the "Retail box" line quantity equal to the number of
 * boxable units in the cart.
 *
 * snippets/box-option.liquid does the counting (product tags are not exposed by
 * /cart.js) and renders two numbers: how many boxes are needed and how many are
 * in the cart. This script only writes when those two disagree, which makes it
 * self-terminating — a write that lands leaves the numbers equal, so the next
 * pass does nothing.
 *
 * Every cart write goes through one promise chain. Shopify's cart endpoints are
 * not concurrency-safe: parallel writes desync the cart.
 */
(function () {
  'use strict';

  var ROOT = '[data-box-option]';
  var SOURCE = 'box-option';
  var MAX_PASSES = 3;

  var chain = Promise.resolve();

  function queue(task) {
    chain = chain.then(task).catch(function (error) {
      console.error('[box-option]', error);
    });
    return chain;
  }

  function route(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }

  function parseState(el) {
    if (!el) return null;
    return {
      variantId: el.dataset.boxVariantId,
      needed: parseInt(el.dataset.boxesNeeded, 10) || 0,
      inCart: parseInt(el.dataset.boxesInCart, 10) || 0,
      boxable: parseInt(el.dataset.boxableUnits, 10) || 0,
      attributeSet: el.dataset.attributeSet === 'true',
      legacyAttribute: el.dataset.legacyAttribute === 'true'
    };
  }

  function sectionIds() {
    var ids = [];
    document.querySelectorAll(ROOT).forEach(function (el) {
      var id = el.dataset.boxSection;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function setBusy(busy) {
    document.querySelectorAll(ROOT).forEach(function (el) {
      el.classList.toggle('is-busy', busy);
    });
  }

  /*
   * Re-render every section that holds a box-option instance and swap in the
   * fresh markup, then hand back the state it reported.
   *
   * The counts have to come off a freshly rendered section rather than the DOM:
   * Dawn only swaps `.js-contents` and the drawer footer on a quantity change,
   * and the checkbox sits outside both, so the in-page data attributes go stale
   * the moment a line quantity changes.
   */
  function refresh() {
    var ids = sectionIds();
    if (!ids.length) return Promise.resolve(null);

    return Promise.all(
      ids.map(function (id) {
        return fetch(route('cart_url', '/cart') + '?section_id=' + encodeURIComponent(id), {
          credentials: 'same-origin'
        })
          .then(function (response) {
            return response.text();
          })
          .then(function (html) {
            return { id: id, doc: new DOMParser().parseFromString(html, 'text/html') };
          });
      })
    ).then(function (results) {
      var state = null;

      results.forEach(function (result) {
        var source = result.doc.querySelector(ROOT);
        if (!source) return;
        if (!state) state = parseState(source);

        document.querySelectorAll(ROOT).forEach(function (target) {
          if (target.dataset.boxSection !== result.id) return;
          target.replaceWith(source.cloneNode(true));
        });

        // Totals live outside the snippet, and a priced box moves them.
        swap(result.doc, '#main-cart-footer .js-contents');
        swap(result.doc, '.cart-drawer__footer');
      });

      return state;
    });
  }

  function swap(doc, selector) {
    var source = doc.querySelector(selector);
    var target = document.querySelector(selector);
    if (source && target) target.innerHTML = source.innerHTML;
  }

  function write(body) {
    return fetch(route('cart_update_url', '/cart/update'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error('cart/update failed: ' + response.status);
      return response.json();
    });
  }

  function announce(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cartData });
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /*
   * One reconcile pass: pull fresh counts, and if they disagree, correct the box
   * line and pull again. MAX_PASSES is a backstop only — a healthy cart settles
   * on the second pass.
   */
  function reconcile(passes) {
    passes = passes || 1;

    return refresh().then(function (state) {
      if (!state || !state.variantId) return null;

      var body = null;

      if (state.needed !== state.inCart) {
        body = { updates: {} };
        body.updates[state.variantId] = state.needed;
      }

      // Stamp the choice on the cart the first time it matters, so it prints on
      // the order and packing slip even when the shopper never touched the box.
      if (!state.attributeSet && state.boxable > 0) {
        body = body || {};
        body.attributes = { Packaging: state.needed > 0 ? 'Boxed' : 'Unboxed' };
      }

      // Empty string deletes a cart attribute. Clears the old checkbox's value
      // off carts that carried it over, so orders print one packaging line.
      if (state.legacyAttribute) {
        body = body || {};
        body.attributes = body.attributes || {};
        body.attributes['Box packaging'] = '';
      }

      if (!body) return state;

      setBusy(true);
      return write(body)
        .then(function (cartData) {
          announce(cartData);
          return passes < MAX_PASSES ? reconcile(passes + 1) : state;
        })
        .finally(function () {
          setBusy(false);
        });
    });
  }

  function toggle(checked) {
    var state = parseState(document.querySelector(ROOT));
    if (!state || !state.variantId) return Promise.resolve();

    // Mirror across every instance immediately — the drawer and the cart page
    // are both on screen at once on /cart.
    document.querySelectorAll('[data-box-option-input]').forEach(function (input) {
      input.checked = checked;
    });

    var quantity = checked ? state.boxable : 0;
    var body = {
      attributes: { Packaging: checked ? 'Boxed' : 'Unboxed', 'Box packaging': '' },
      updates: {}
    };
    body.updates[state.variantId] = quantity;

    setBusy(true);
    return write(body)
      .then(function (cartData) {
        announce(cartData);
        return reconcile();
      })
      .finally(function () {
        setBusy(false);
      });
  }

  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-box-option-input]')) return;
    var checked = event.target.checked;
    queue(function () {
      return toggle(checked);
    });
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === SOURCE) return;
      queue(function () {
        return reconcile();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      queue(function () {
        return reconcile();
      });
    });
  } else {
    queue(function () {
      return reconcile();
    });
  }
})();
